import type { TourBasicInfoState } from '@/shared/api/types/tour-types'
import { Loader2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ALL_FILTER_VALUE } from '@/shared/constants/filters'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface SelectOption {
  label: string
  value: string
}

interface TourScopeFieldsProps {
  value: TourBasicInfoState
  countryOptions: SelectOption[]
  provinceOptions: SelectOption[]
  tenantDestinationOptions: SelectOption[]
  isProvinceLoading?: boolean
  isTenantDestinationLoading?: boolean
  onChange: (value: TourBasicInfoState) => void
  onCreateDestination?: () => void
}

/** Country → province → tenant destination: each choice narrows the next, and the destination decides which POIs can be picked. */
export function TourScopeFields({
  value,
  countryOptions,
  provinceOptions,
  tenantDestinationOptions,
  isProvinceLoading,
  isTenantDestinationLoading,
  onChange,
  onCreateDestination,
}: TourScopeFieldsProps) {
  const { t } = useTranslation()
  const chooseLabel = t('common.select', { defaultValue: 'Chọn' })
  const hasNoDestination =
    Boolean(value.countryId && value.provinceId) &&
    !isTenantDestinationLoading &&
    tenantDestinationOptions.length === 0

  return (
    <div className="space-y-5">
      <p className="text-sm text-zinc-500">
        {t('tour.scopeDestinationHint', {
          defaultValue: 'Chọn điểm đến cụ thể để chỉ lấy POI thuộc khu đó.',
        })}
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t('destination.country')} required>
          <Select
            value={value.countryId || ALL_FILTER_VALUE}
            onValueChange={(nextValue) => {
              const countryId = nextValue === ALL_FILTER_VALUE ? '' : nextValue
              onChange({ ...value, countryId, provinceId: '', tenantDestinationId: '' })
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('destination.country')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FILTER_VALUE}>{chooseLabel}</SelectItem>
              {countryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label={t('destination.province')} required>
          <Select
            value={value.provinceId || ALL_FILTER_VALUE}
            onValueChange={(nextValue) => {
              const provinceId = nextValue === ALL_FILTER_VALUE ? '' : nextValue
              onChange({ ...value, provinceId, tenantDestinationId: '' })
            }}
            disabled={!value.countryId || Boolean(isProvinceLoading)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('destination.province')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FILTER_VALUE}>{chooseLabel}</SelectItem>
              {provinceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <FormField label={t('poi.destination')} required>
        <Select
          value={value.tenantDestinationId || ALL_FILTER_VALUE}
          onValueChange={(nextValue) =>
            onChange({
              ...value,
              tenantDestinationId: nextValue === ALL_FILTER_VALUE ? '' : nextValue,
            })
          }
          disabled={!value.countryId || !value.provinceId || Boolean(isTenantDestinationLoading)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('poi.selectDestination')} />
          </SelectTrigger>
          <SelectContent>
            {isTenantDestinationLoading ? (
              <div className="flex items-center justify-center gap-2 px-2 py-6 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" />
              </div>
            ) : (
              <>
                <SelectItem value={ALL_FILTER_VALUE}>{chooseLabel}</SelectItem>
                {tenantDestinationOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>

        {/* * VI: Danh sách điểm đến được lọc theo tỉnh/thành; khi rỗng phải báo rõ lý do và đưa luôn lối tạo mới thay vì để ô trống. | EN: The destination list is filtered by province; when empty, explain why and offer "create" right there instead of leaving the field blank. */}
        {hasNoDestination && (
          <p className="mt-2 text-xs text-amber-700">
            {t('tour.noDestinationInProvince', {
              defaultValue: 'Không có điểm đến nào thuộc tỉnh/thành bạn đã chọn.',
            })}
          </p>
        )}
        {onCreateDestination && !isTenantDestinationLoading && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 -ml-3 text-primary hover:text-primary"
            onClick={onCreateDestination}
          >
            <Plus />
            {t('poi.destinationCreateAction', { defaultValue: 'Create new destination' })}
          </Button>
        )}
      </FormField>
    </div>
  )
}
